import React, { useState, useRef, useCallback, useEffect } from 'react';
import Map, { Marker } from 'react-map-gl/mapbox';
import { MapPinIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import 'mapbox-gl/dist/mapbox-gl.css';

const AddressAutocomplete = ({
    onAddressSelect,
    placeholder = 'Escribe una dirección...',
    initialQuery = '',
    initialSelected = null
}) => {
    const [query, setQuery] = useState(initialQuery);
    const [suggestions, setSuggestions] = useState([]);
    const [mapboxResult, setMapboxResult] = useState(initialSelected);
    const [distritos, setDistritos] = useState(null); // null = not yet fetched
    const [selectedDistrito, setSelectedDistrito] = useState(null);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [loadingDistritos, setLoadingDistritos] = useState(false);
    const debounceRef = useRef(null);

    // On mount: if initialSelected, load districts for display and pre-select
    useEffect(() => {
        if (!initialSelected?.municipio) return;
        setLoadingDistritos(true);
        fetch(`${window.REACT_APP_API_ENDPOINT}/api/wizard/localizaciones/distritos?municipio=${encodeURIComponent(initialSelected.municipio)}`)
            .then(r => r.json())
            .then(data => {
                const lista = data.distritos || [];
                setDistritos(lista);
                if (initialSelected.distrito && lista.length) {
                    const match = lista.find(d => d.distrito?.toLowerCase() === initialSelected.distrito.toLowerCase());
                    if (match) setSelectedDistrito(match);
                }
            })
            .catch(() => setDistritos([]))
            .finally(() => setLoadingDistritos(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchSuggestions = useCallback(async (value) => {
        if (!value || value.length < 3) { setSuggestions([]); return; }
        const token = window.MAPBOX_TOKEN;
        if (!token || token === 'pk.YOUR_MAPBOX_TOKEN_HERE') return;
        setLoadingSuggestions(true);
        try {
            const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(value)}.json?country=es&language=es&types=address&access_token=${token}`;
            const res = await fetch(url);
            const data = await res.json();
            setSuggestions(data.features || []);
        } catch {
            setSuggestions([]);
        } finally {
            setLoadingSuggestions(false);
        }
    }, []);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setQuery(value);
        setMapboxResult(null);
        setDistritos(null);
        setSelectedDistrito(null);
        onAddressSelect(null);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchSuggestions(value), 300);
    };

    const handleSelect = async (result) => {
        const [lon, lat] = result.center;
        const context = result.context || [];
        const codigoPostal = context.find(c => c.id.startsWith('postcode'))?.text || null;
        const mapboxLocality = context.find(c =>
            c.id.startsWith('locality') || c.id.startsWith('neighborhood')
        )?.text || null;
        const municipio = context.find(c => c.id.startsWith('place'))?.text || null;

        const base = { direccion: query, municipio, codigoPostal, lat, lon };
        setMapboxResult(base);
        setSuggestions([]);
        setDistritos(null);
        setSelectedDistrito(null);
        onAddressSelect(null); // invalidate until district confirmed

        if (!municipio) return;

        setLoadingDistritos(true);
        try {
            const res = await fetch(
                `${window.REACT_APP_API_ENDPOINT}/api/wizard/localizaciones/distritos?municipio=${encodeURIComponent(municipio)}`
            );
            const data = await res.json();
            const lista = data.distritos || [];
            setDistritos(lista);

            if (lista.length === 0) {
                // No districts in DB — pass without district (report will lack price analysis)
                onAddressSelect({ ...base, distrito: null });
                return;
            }

            // Auto-select if Mapbox locality matches a DB district
            if (mapboxLocality) {
                const match = lista.find(d => d.distrito?.toLowerCase() === mapboxLocality.toLowerCase());
                if (match) {
                    setSelectedDistrito(match);
                    onAddressSelect({ ...base, distrito: match.distrito });
                }
            }
        } catch {
            setDistritos([]);
            onAddressSelect({ ...base, distrito: null });
        } finally {
            setLoadingDistritos(false);
        }
    };

    const handleDistritoSelect = (d) => {
        setSelectedDistrito(d);
        if (mapboxResult) {
            onAddressSelect({ ...mapboxResult, distrito: d.distrito });
        }
    };

    const handleClear = () => {
        setMapboxResult(null);
        setDistritos(null);
        setSelectedDistrito(null);
        setQuery('');
        setSuggestions([]);
        onAddressSelect(null);
    };

    const showDistritoSelector = mapboxResult && distritos && distritos.length > 0;
    const noDistritosAvailable = mapboxResult && distritos !== null && distritos.length === 0;

    return (
        <div className="space-y-4">
            {/* Address input */}
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                    type="text"
                    value={query}
                    onChange={handleInputChange}
                    placeholder={placeholder}
                    className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {query && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                )}

                {suggestions.length > 0 && !mapboxResult && (
                    <ul className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {suggestions.map((s) => (
                            <li
                                key={s.id}
                                onClick={() => handleSelect(s)}
                                className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-blue-50 text-sm border-b border-gray-100 last:border-b-0"
                            >
                                <MapPinIcon className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-700">{s.place_name}</span>
                            </li>
                        ))}
                    </ul>
                )}

                {loadingSuggestions && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-sm px-4 py-3 text-sm text-gray-500">
                        Buscando...
                    </div>
                )}
            </div>

            {/* Confirmation pill */}
            {mapboxResult && (
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-800 text-sm font-medium px-3 py-1.5 rounded-full">
                        <MapPinIcon className="h-4 w-4" />
                        {[mapboxResult.municipio, mapboxResult.codigoPostal].filter(Boolean).join(' · ')}
                    </span>
                    <button
                        type="button"
                        onClick={handleClear}
                        className="text-sm text-gray-500 underline hover:text-gray-700"
                    >
                        Cambiar
                    </button>
                </div>
            )}

            {/* District selector */}
            {loadingDistritos && (
                <p className="text-xs text-gray-400">Cargando zonas disponibles...</p>
            )}

            {showDistritoSelector && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Barrio / Distrito <span className="text-red-500">*</span>
                        <span className="text-xs font-normal text-gray-400 ml-1">— necesario para comparar precios de zona</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                        {distritos.map(d => (
                            <button
                                key={d.id}
                                type="button"
                                onClick={() => handleDistritoSelect(d)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                                    selectedDistrito?.id === d.id
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                                }`}
                            >
                                {d.distrito}
                            </button>
                        ))}
                    </div>
                    {!selectedDistrito && (
                        <p className="text-xs text-amber-600 mt-1.5">Selecciona el barrio para continuar</p>
                    )}
                </div>
            )}

            {noDistritosAvailable && (
                <p className="text-xs text-gray-400">
                    Esta zona no tiene datos de precios todavía. El informe incluirá el análisis de reforma sin comparativa de mercado.
                </p>
            )}

            {/* Map */}
            {mapboxResult && (
                <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm" style={{ height: 240 }}>
                    <Map
                        mapboxAccessToken={window.MAPBOX_TOKEN}
                        initialViewState={{
                            longitude: mapboxResult.lon,
                            latitude: mapboxResult.lat,
                            zoom: 15
                        }}
                        mapStyle="mapbox://styles/mapbox/streets-v12"
                        style={{ width: '100%', height: '100%' }}
                    >
                        <Marker longitude={mapboxResult.lon} latitude={mapboxResult.lat} anchor="bottom">
                            <MapPinIcon className="h-8 w-8 text-blue-600 drop-shadow" />
                        </Marker>
                    </Map>
                </div>
            )}
        </div>
    );
};

export default AddressAutocomplete;
